import InputAdornment from '@mui/material/InputAdornment';
import React, { CSSProperties, FC, useCallback, useMemo, useRef, useState } from 'react';
import { styled } from '../../styles';
import { tokens } from '../../theme';
import { useIntlBds } from '../../translations/BdsIntlProvider';
import { BpDesignSystem } from '../../types';
import { Keys } from '../../utils/keysUtil';
import { Box } from '../Box/Box';
import { Embolden } from '../Embolden';
import { FormHelperText } from '../Form/FormHelperText';
import { Input } from '../Form/Input';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Menu } from '../Menu';
import { caretXY } from './caret-xy';

export type MenuPopperProps = BpDesignSystem.Props<{
  children?: React.ReactNode;
  onChange: (value: string, charPosition: number) => void;
  values: string[];
  filterProps?: {
    placeholder?: string;
    label?: string;
    className?: string;
    style?: CSSProperties;
  };
  menuProps?: {
    style?: CSSProperties;
    className?: string;
  };
  paperProps?: {
    style?: CSSProperties;
    className?: string;
  };
  helperText?: React.ReactNode;
}>;

/**
 *
 * Menu Popper is a component to display a searchable and keyboard
 * accessible pop-up menu that follows the current cursor position
 * within an input or text area field.
 *
 * */
export const MenuPopper: FC<React.PropsWithChildren<MenuPopperProps>> = ({
  children,
  onChange,
  values,
  className,
  sx,
  dataTest = 'menu-popper',
  style,
  filterProps,
  menuProps,
  paperProps,
  helperText
}) => {
  const { intl } = useIntlBds();
  const [coord, setCoord] = useState<any>();
  const menuRef = useRef<HTMLInputElement | null>(null);
  const filterClearRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLDivElement | null>(null);
  const firstMenuItem = useRef<HTMLLIElement | null>(null);
  const [selectionStart, setSelectionStart] = useState<number>(0);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const sortedValues = useMemo(
    () =>
      values
        ?.filter((value) => value.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()))
        .sort((a, b) => a.localeCompare(b)),
    [searchTerm, values]
  );

  const focusSearchInput = useCallback(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    });
  }, []);

  const blurSearchInput = useCallback(() => {
    inputRef.current?.blur();
  }, []);

  const handleMenuPopperKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === '[' && !anchorEl) {
        setSearchTerm('');
        // Prevent [ character appearing in the search input.
        event.preventDefault();
        event.stopPropagation();

        const isInput = (event.target as HTMLElement).tagName.toLocaleLowerCase() === 'input';
        // Save properties.
        const targetEl = event.target as HTMLElement;
        const node = !isInput ? (window.getSelection()?.anchorNode as ChildNode) : null;

        setScrollPosition(targetEl.scrollTop);
        focusSearchInput();
        setAnchorEl(targetEl);
        const { x, y, selectionStart } = caretXY(targetEl); // depends on caret position it returns caret position for input/textarea or contenteditable text node
        let prevChildrenLength = 0;

        // Calculate offset for text node within contenteditable when there are more than 1 text node. This could happen when user during typing adds line breaks.
        if (node && targetEl.childNodes.length > 1) {
          let isPrevious = true;
          targetEl.childNodes.forEach((it) => {
            if (it === node) {
              isPrevious = false;
            }
            if (isPrevious) {
              prevChildrenLength += it.textContent?.length ?? 0;
            }
          });
        }
        setSelectionStart(prevChildrenLength + selectionStart);
        setCoord({ x, y });
      }
    },
    [anchorEl, focusSearchInput]
  );

  const handleMenuItemSelect = useCallback(
    (value: string) => {
      blurSearchInput();
      // Apply change and update cursor position.
      const label = !!value ? `[${value}] ` : undefined;
      if (!!label) {
        onChange(label, selectionStart);

        setTimeout(() => {
          if (anchorEl) {
            anchorEl.scrollTop = scrollPosition;
          }
          const cursorPosition = selectionStart + (label ? label.length : 0);
          const tagName = anchorEl?.tagName?.toLocaleLowerCase() ?? '';

          if (tagName === 'input') {
            anchorEl?.focus();
            (anchorEl as HTMLInputElement | HTMLTextAreaElement | undefined)?.setSelectionRange(
              cursorPosition,
              cursorPosition
            );
          } else if (!!tagName) {
            const sel = window.getSelection();
            if (sel) {
              sel.removeAllRanges();
              const rng = document.createRange();
              // at this point, we know that anchorEl is contenteditable and has just one text node child
              const txtNode = anchorEl?.firstChild;
              if (txtNode) {
                rng.setStart(txtNode, cursorPosition);
                rng.setEnd(txtNode, cursorPosition);
                sel.addRange(rng);
              }
              anchorEl?.focus();
            }
          }
        }, 50); // Wait for change to take effect.
      }

      setAnchorEl(null);
    },
    [anchorEl, onChange, scrollPosition, selectionStart, blurSearchInput]
  );

  const handleCloseMenu = useCallback(() => {
    handleMenuItemSelect('');
  }, [handleMenuItemSelect]);

  const handleClearSearch = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setSearchTerm('');
      focusSearchInput();
    },
    [focusSearchInput]
  );

  const focusFirstItem = useCallback(() => {
    const element = firstMenuItem.current;
    if (element) {
      setTimeout(() => {
        element?.focus();
      });
    }
    return element;
  }, []);

  const focusClearSearchInputOrFirstItem = useCallback(() => {
    if (filterClearRef.current) {
      filterClearRef.current?.focus();
    } else {
      focusFirstItem();
    }
  }, [focusFirstItem]);

  const handleSearchByKeyboard = useCallback(
    (event: React.KeyboardEvent) => {
      if (Keys.oneOf(event, 'ArrowDown')) {
        event.stopPropagation();
        event.preventDefault();
        focusFirstItem();
        return;
      }
      if (Keys.oneOf(event, 'Escape')) {
        event.stopPropagation();
        event.preventDefault();
        handleCloseMenu();
        return;
      }
      if (Keys.oneOf(event, 'Tab')) {
        event.stopPropagation();
        event.preventDefault();
        focusClearSearchInputOrFirstItem();
        return;
      }
      if (Keys.oneOf(event, 'Enter')) {
        const firstElement = focusFirstItem();
        if (firstElement) {
          // Select first item on enter.
          event.stopPropagation();
          event.preventDefault();
          handleMenuItemSelect(firstElement.textContent ?? '');
        } else {
          // Don't close the menu on enter if no items in menu.
          event.stopPropagation();
          event.preventDefault();
        }
      } else {
        event.stopPropagation();
      }
    },
    [handleMenuItemSelect, handleCloseMenu, focusFirstItem, focusClearSearchInputOrFirstItem]
  );

  const handleSearchValueChange = useCallback(
    (term: string) => {
      setSearchTerm(term);
      setAnchorEl(anchorEl);
      focusSearchInput();
    },
    [anchorEl, focusSearchInput]
  );

  const handleMenuKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (Keys.oneOf(e, 'Escape')) {
        handleCloseMenu();
        return;
      }
    },
    [handleCloseMenu]
  );

  return (
    <Box data-test={dataTest} onKeyDown={handleMenuPopperKeyDown} className={className} style={style} sx={sx}>
      <Menu
        elevation={0}
        slotProps={{
          paper: {
            // @ts-ignore valid prop
            'data-test': 'select-with-filter-menu',
            className: paperProps?.className,
            style: {
              border: `1px solid ${tokens.colorSurfaceVariant}`,
              boxShadow: `0px 3px 10px rgba(0, 0, 0, 0.06)`,
              marginTop: -1,
              maxHeight: 300,
              ...paperProps?.style
            }
          }
        }}
        className={menuProps?.className}
        anchorPosition={{ top: coord?.y ?? 0, left: coord?.x ?? 0 }}
        anchorReference="anchorPosition"
        style={{
          border: `1px solid ${tokens.colorSurfaceVariant}`,
          boxShadow: `0px 3px 10px rgba(0, 0, 0, 0.06)`,
          marginTop: -1,
          ...menuProps?.style
        }}
        id={`${dataTest}-menu`}
        anchorEl={anchorEl}
        open={!!anchorEl}
        ref={menuRef}
        onKeyDown={handleMenuKeyDown}
        onClose={() => {
          handleCloseMenu();
        }}
        disableAutoFocus
        disablePortal
        disableAutoFocusItem
        disableEnforceFocus
      >
        <StyledFilterWrapper
          onClick={(e) => {
            e.stopPropagation();
          }}
          onFocus={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if ((e.target as HTMLElement) !== (filterClearRef.current as HTMLElement)) {
              focusSearchInput();
            }
          }}
        >
          <Input
            value={searchTerm}
            label={filterProps?.label ?? ''}
            inputRef={inputRef}
            dataTest={`${dataTest ?? 'select'}-search`}
            className={filterProps?.className}
            startAdornment={<Icon name="search" />}
            inputMode="search"
            endAdornment={
              searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    iconName="close"
                    onClick={handleClearSearch}
                    onKeyDown={handleClearSearch}
                    ref={filterClearRef}
                  />
                </InputAdornment>
              ) : null
            }
            slotProps={{
              input: {
                style: filterProps?.style
              }
            }}
            fullWidth
            autoFocus
            placeholder={filterProps?.placeholder ?? ''}
            onChange={(e) => handleSearchValueChange(e.target.value)}
            onKeyDown={handleSearchByKeyboard}
          />
        </StyledFilterWrapper>
        {sortedValues?.map((value, index) => {
          return (
            <Menu.Item
              key={value}
              ref={index === 0 ? firstMenuItem : undefined}
              dataTest={`menu-popper-item-${index}`}
              onClick={(e) => {
                handleMenuItemSelect(value);
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <Menu.ItemText
                slotProps={{
                  primary: {
                    component: 'div'
                  }
                }}
                primary={<Embolden term={searchTerm}>{value}</Embolden>}
              />
            </Menu.Item>
          );
        })}
      </Menu>

      {children}
      <FormHelperText
        helperText={helperText ?? intl.MenuPopper.hintText}
        dataTest={`${dataTest ?? 'menu-popper'}-helper-text`}
      />
    </Box>
  );
};

const StyledFilterWrapper = styled('div')`
    padding: 8px 16px 16px 16px;
    outline: none;
`;

export const inplaceString = (target: string, charPosition: number, value: string) => {
  const characters: string[] = `${target}`.split('');
  characters.splice(charPosition, 0, value);
  return characters.join('');
};
